import { ReprintCidFormVM } from 'front-office/view-models/reprint-cid/form';
import { DateTime } from 'luxon';
import {
  RequestPrintingCardboard,
  RequestPrintingEnvelope,
  RequestPrintingExternal,
  RequestPrintingInterior,
} from 'shell/models/request-reprint-cid';

const formattedMarriage = (values: ReprintCidFormVM) => {
  if (values?.person?.marriage?.coniuge?.cognome || values?.person?.marriage?.coniuge?.nome) {
    // marimonio
    return `${values?.person?.marriage?.coniuge?.cognome || ''} ${
      values?.person?.marriage?.coniuge?.nome || ''
    }`;
  }

  if (
    values?.person?.civilUnion?.unitoCivilmente?.cognome ||
    values?.person?.civilUnion?.unitoCivilmente?.nome
  ) {
    // unione civile
    return `${values?.person?.civilUnion?.unitoCivilmente?.cognome || ''} ${
      values?.person?.civilUnion?.unitoCivilmente?.nome || ''
    }`;
  }

  return undefined;
};

const formattedDateMarriage = (values: ReprintCidFormVM) => {
  if (values?.person?.marriage?.datiMatrimonio?.dataEvento) {
    // matrimonio
    return DateTime.fromISO(values?.person?.marriage?.datiMatrimonio?.dataEvento)?.toFormat(
      'dd/MM/yyyy'
    );
  }

  if (values?.person?.civilUnion?.attoUnione?.dataEvento) {
    // unione civile
    return DateTime.fromISO(values?.person?.civilUnion?.attoUnione?.dataEvento)?.toFormat(
      'dd/MM/yyyy'
    );
  }

  return undefined;
};

const formattedMunicipalityMarriage = (values: ReprintCidFormVM) => {
  if (values?.person?.marriage?.datiMatrimonio?.luogoEvento?.comune) {
    // matrimonio
    return values?.person?.marriage?.datiMatrimonio?.luogoEvento?.comune?.denominazione || '';
  }

  if (values?.person?.marriage?.datiMatrimonio?.luogoEvento?.localita) {
    // matrimonio
    return (
      values?.person?.marriage?.datiMatrimonio?.luogoEvento?.localita?.descrizioneLocalita || ''
    );
  }

  if (values?.person?.marriage?.datiMatrimonio?.luogoEvento?.luogoEccezionale) {
    // matrimonio
    return values?.person?.marriage?.datiMatrimonio?.luogoEvento?.luogoEccezionale || '';
  }

  if (values?.person?.civilUnion?.attoUnione?.luogoEvento?.comune) {
    // unione civile
    return values?.person?.civilUnion?.attoUnione?.luogoEvento?.comune?.denominazione || '';
  }

  if (values?.person?.civilUnion?.attoUnione?.luogoEvento?.localita) {
    // unione civile
    return values?.person?.civilUnion?.attoUnione?.luogoEvento?.localita?.descrizioneLocalita || '';
  }

  if (values?.person?.civilUnion?.attoUnione?.luogoEvento?.luogoEccezionale) {
    // unione civile
    return values?.person?.civilUnion?.attoUnione?.luogoEvento?.luogoEccezionale || '';
  }
};

export const transformPrintingEnvelope = (values: ReprintCidFormVM): RequestPrintingEnvelope => {
  const request: RequestPrintingEnvelope = {
    dataRilascio: values.identityCard?.dateRelease
      ? values.identityCard?.dateRelease?.toFormat('yyyyMMdd')
      : undefined,
    dataScadenza: values.identityCard?.dateExpiration
      ? values.identityCard?.dateExpiration?.toFormat('yyyyMMdd')
      : undefined,
    interdizioneEspatrio: values.identityCard?.isExpatriationBan ? 'S' : 'N',
    motivoRilascio: values.identityCard?.reasonRelease || undefined,
    numeroCarta: values.identityCard?.number || undefined,
    cartaceaElettronica: values.identityCard?.type || undefined,
    tipoPagamento: values.identityCard?.paymentType || undefined,
    registroDocRilasciati: values.person?.registroDocRilasciati
      ? {
          ...(values.person?.registroDocRilasciati || {}),
          timestampRilascio:
            values.identityCard?.dateContabilizzazione?.toISO({ includeOffset: false }) || '',
        }
      : {
          timestampRilascio:
            values.identityCard?.dateContabilizzazione?.toISO({ includeOffset: false }) || '',
        },
    cittadinanza: values.person?.citizenship ? [values.person?.citizenship] : undefined,
    datiIscrizione: values.person?.registrationData || undefined,
    residenza: values.person?.residenceToRequest ? values.person?.residenceToRequest : undefined,
    generalitaRichiedente: values.person?.generality
      ? {
          ...values.person?.generality,
          idSchedaSoggettoComune:
            values.person.generality?.idSchedaSoggettoComune || values.person?.codicePersonaSipo
              ? {
                  ...(values.person.generality?.idSchedaSoggettoComune || {}),
                  idSchedaSoggetto:
                    values.person.generality?.idSchedaSoggettoComune
                      ?.idSchedaSoggettoComuneIstat === '015146' ||
                    !values.person.generality?.idSchedaSoggettoComune?.idSchedaSoggetto
                      ? values.person?.codicePersonaSipo
                      : values.person.generality?.idSchedaSoggettoComune?.idSchedaSoggetto ||
                        values.person?.codicePersonaSipo ||
                        undefined,
                  idSchedaSoggettoComuneIstat:
                    values.person.generality?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat ||
                    undefined,
                }
              : undefined,
        }
      : {},
  };

  return request;
};

export const transformPrintingCardboard = (values: ReprintCidFormVM): RequestPrintingCardboard => {
  const request: RequestPrintingCardboard = {
    cdCertificato: 'CID01',
    numeroCI: values?.identityCard?.number || undefined,
    comuneRilascio: values?.identityCard?.municipality || undefined,
    motivoRilascio: values?.identityCard?.reasonRelease,
    resaSiNo: values?.identityCard?.isReturnedOld ? 'S' : 'N',
    comuneRilascioprec: values?.identityCard?.oldMunicipality || '', // TODO chiedere
    numeroCIprec: values?.identityCard?.oldCard || undefined,
    dataRilascioCi: values?.identityCard?.dateRelease?.toFormat('dd/MM/yyyy') || undefined,
    cognome: values?.person?.surname || undefined,
    nome: values?.person?.name || undefined,
    paternita: values?.person?.paternity?.generalita?.nome || undefined,
    maternita: `${values?.person?.maternity?.generalita?.cognome || ''} ${
      values?.person?.maternity?.generalita?.nome || ''
    }`,
    dataNascita: values?.person?.dateBorn?.toFormat('dd/MM/yyyy') || undefined,
    luogoNascita: values?.person?.placeBorn || undefined,
    attonascita: values?.person?.actBorn?.numeroAtto || undefined,
    statoCivile: values.person?.civilStatus?.statoCivile || undefined,
    coniuge: formattedMarriage(values),
    dataMatrimonio: formattedDateMarriage(values),
    comuneMatrimonio: formattedMunicipalityMarriage(values),
    attoMatrimonio:
      values.person?.marriage?.datiMatrimonio?.attoEventoANPR?.atto?.numeroAtto || undefined,
    cittadinanza: values?.person?.citizenship?.codiceStato || undefined,
    dataIscrizione:
      values?.person?.registrationData?.datiIscrizioneComune?.dataIscrizione || undefined, // come formattare
    comuneProvenienza:
      values?.person?.registrationData?.datiIscrizioneComune?.comune?.nomeComune || undefined,
    professione: values?.person?.otherDatas?.posizioneProfessionale || undefined,
    capelli: values?.identityCard?.hair || undefined,
    statura: values?.identityCard?.stature || undefined,
    occhi: values?.identityCard?.eyes || undefined,
    segniparticolari: values?.identityCard?.particularSigns || undefined,
    dirittiFissi: (values.identityCard?.fixedRights || 0)?.toString(),
    dirittiSegreteria: (values.identityCard?.secretarialRights || 0)?.toString(),
  };

  return request;
};

export const transformPrintingInterior = (values: ReprintCidFormVM): RequestPrintingInterior => {
  const request: RequestPrintingInterior = {
    cdCertificato: 'CID02',
    cognome: values?.person?.surname || undefined,
    nome: values?.person?.name || undefined,
    dataNascita: values?.person?.dateBorn?.toFormat('dd/MM/yyyy') || undefined,
    luogoNascita: values?.person?.placeBorn || undefined,
    cittadinanza: values?.person?.citizenship?.codiceStato || undefined,
    attonascita: values?.person?.actBorn?.numeroAtto || undefined,
    comuneResidenza:
      values.person?.residence?.location || values.person?.residence?.locationForeign || undefined,
    indirizzoResidenza: `${values.person?.residence?.kind || ''} ${
      values.person?.residence?.name
    } ${values.person?.residence?.number || ''} ${values.person?.residence?.exponent || ''} ${
      values.person?.residence?.letter || ''
    }`,
    statoCivile: values.person?.civilStatus?.statoCivile || undefined,
    professione: values?.person?.otherDatas?.posizioneProfessionale || undefined,
    dataRilascio: values?.identityCard?.dateRelease?.toFormat('dd/MM/yyyy') || undefined,
    capelli: values?.identityCard?.hair || undefined,
    statura: values?.identityCard?.stature || undefined,
    occhi: values?.identityCard?.eyes || undefined,
    segniparticolari: values?.identityCard?.particularSigns || undefined,
    diritti: (
      (values.identityCard?.fixedRights || 0) + (values.identityCard?.secretarialRights || 0)
    ).toString(),
    sesso: values?.person?.gender || undefined,
  };

  return request;
};

export const transformPrintingExternal = (values: ReprintCidFormVM): RequestPrintingExternal => {
  const request: RequestPrintingExternal = {
    cdCertificato: 'CID03',
    cognomeRichiedente: values.person?.name || undefined,
    nomeRichiedente: values.person?.name || undefined,
    comuneRilascio: values?.identityCard?.municipality || undefined,
    dataScadenza: values?.identityCard?.dateExpiration?.toFormat('yyyy-MM-dd') || undefined,
    cognomePadre: values?.person?.paternity?.generalita?.cognome || undefined,
    nomePadre: values?.person?.paternity?.generalita?.nome || undefined,
    cognomeMadre: values?.person?.maternity?.generalita?.cognome || undefined,
    nomeMadre: values?.person?.maternity?.generalita?.nome || undefined,
    sesso: values?.person?.gender || undefined,
    numeroCI: values?.identityCard?.number || undefined,
    interdizioneEspatrio: values.identityCard?.isExpatriationBan ? 'S' : 'N',
  };

  return request;
};
